import { type CallWindow, type Event } from '@kalos/kalos-rpc';
import { format, isValid, parseISO } from 'date-fns';

/**
 * Centralized time calculation and validation utilities
 *
 * This module provides time conversion and validation functions for scheduling.
 * Supports overnight bookings (e.g., 6pm-3am spans to the next day).
 */

/**
 * Normalize time string to HH:mm format (removes seconds if present).
 * Examples:
 * - "08:30:00" → "08:30"
 * - "14:45" → "14:45"
 * - "09:00:59" → "09:00"
 */
export const normalizeTime = (time: string): string => {
  return time.slice(0, 5);
};

/** Call windows and job times are scheduled on a 15-minute grid. */
export const TIME_STEP_MINUTES = 15;
const MAX_MINUTES_OF_DAY = 24 * 60 - TIME_STEP_MINUTES;
const TIME_ANCHOR_DAY = '2000-01-01';

/**
 * Snap a time string to the nearest TIME_STEP_MINUTES boundary.
 * Examples: "13:37" → "13:30", "12:55" → "13:00", "23:59" → "23:45"
 */
export const snapToStep = (time: string): string => {
  const [hours, minutes] = normalizeTime(time).split(':').map(Number);
  if (Number.isNaN(hours) || Number.isNaN(minutes)) return time;
  const snapped = Math.min(
    Math.round((hours * 60 + minutes) / TIME_STEP_MINUTES) * TIME_STEP_MINUTES,
    MAX_MINUTES_OF_DAY,
  );
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${pad(Math.floor(snapped / 60))}:${pad(snapped % 60)}`;
};

/** Time-of-day string to a Date on a fixed anchor day, for the TimePicker component. */
export const timeToDate = (time: string): Date | undefined => {
  if (!time) return undefined;
  const parsed = parseISO(`${TIME_ANCHOR_DAY}T${snapToStep(time)}:00`);
  return isValid(parsed) ? parsed : undefined;
};

/** Inverse of timeToDate — back to an HH:mm string. */
export const dateToTime = (date: Date | undefined): string =>
  date && isValid(date) ? format(date, 'HH:mm') : '';
/**
 * Convert time string (HH:mm or HH:mm:ss) to minutes since midnight
 */
export const timeToMinutes = (time: string): number => {
  const [hours, minutes] = time.split(':').map(Number);
  return hours * 60 + minutes;
};

/**
 * Check if a time range is overnight (end time is on the next calendar day).
 * Examples:
 * - 18:00-03:00 → overnight (ends next day at 3am)
 * - 11:00-02:00 → overnight (ends next day at 2am)
 * - 08:00-17:00 → NOT overnight (same day)
 * - 20:00-00:00 → overnight (midnight = start of next day)
 */
export const isOvernightRange = (startTime: string, endTime: string): boolean => {
  const startMinutes = timeToMinutes(startTime);
  const endMinutes = timeToMinutes(endTime);
  // Overnight if end is at or before start (wraps to next day)
  // 00:00 (midnight) as end time = start of next day, so it's overnight unless start is also 00:00
  return endMinutes <= startMinutes;
};

/**
 * Add one day to a date string (yyyy-MM-dd format)
 */
const addOneDay = (dateStr: string): string => {
  const date = new Date(dateStr + 'T00:00:00');
  date.setDate(date.getDate() + 1);
  return format(date, 'yyyy-MM-dd');
};

/**
 * Check if a time RANGE is past (both start and end have passed).
 * Handles overnight bookings correctly - end time is on the NEXT day.
 *
 * For overnight range 6pm-3am starting today:
 * - If it's 5pm today → NOT past (hasn't started)
 * - If it's 7pm today → NOT past (ongoing, ends tomorrow at 3am)
 * - If it's 2am tomorrow → NOT past (ongoing, ends at 3am)
 * - If it's 4am tomorrow → PAST (ended at 3am)
 *
 * @param startTime - Start time in HH:mm format
 * @param endTime - End time in HH:mm format
 * @param startDate - The date the time range STARTS (yyyy-MM-dd)
 * @param currentDate - Current date (yyyy-MM-dd)
 * @param currentTimeInMinutes - Current time in minutes since midnight
 */
export const isTimeRangePast = (
  startTime: string,
  endTime: string,
  startDate: string,
  currentDate: string,
  currentTimeInMinutes: number,
): boolean => {
  const overnight = isOvernightRange(startTime, endTime);
  const endMinutes = timeToMinutes(endTime);

  if (overnight) {
    // End time is on the NEXT calendar day
    const endDate = addOneDay(startDate);

    if (currentDate < startDate) {
      // Today is before the range starts
      return false;
    } else if (currentDate === startDate) {
      // Today is the start date - the range ends tomorrow, so NOT past
      return false;
    } else if (currentDate === endDate) {
      // Today is the end date - check if we're past the end time
      return currentTimeInMinutes >= endMinutes;
    } else {
      // Today is after the end date
      return true;
    }
  } else {
    // Same-day range - both start and end are on startDate
    if (currentDate < startDate) {
      return false;
    } else if (currentDate > startDate) {
      return true;
    } else {
      // Same day - check if end time has passed
      return currentTimeInMinutes >= endMinutes;
    }
  }
};

/**
 * Check if a time is past for a given date (for start times)
 */
export const isTimePast = (
  time: string,
  dateToCheck: string,
  currentDate: string,
  currentTimeInMinutes: number,
): boolean => {
  if (dateToCheck < currentDate) return true;
  if (dateToCheck > currentDate) return false;
  return timeToMinutes(time) <= currentTimeInMinutes;
};

/**
 * Check if a call window's start time is past for a given date.
 * Allows booking within the same clock hour as the window's start
 * (e.g., a 2-6 PM window remains bookable up until 3:00 PM).
 */
export const isWindowPast = (
  window: CallWindow,
  selectedDate: string,
  currentDate: string,
  currentTimeInMinutes: number,
): boolean => {
  if (selectedDate < currentDate) return true;
  if (selectedDate > currentDate) return false;
  return currentTimeInMinutes >= timeToMinutes(window.startTime) + 60;
};

/**
 * Check if an event's start time falls within a call window.
 * Handles windows that cross midnight (e.g., 22:00-02:00).
 */
export const isEventInWindow = (event: Event, window: CallWindow): boolean => {
  if (!event.timeStarted) return false;

  const eventMinutes = timeToMinutes(event.timeStarted);
  const windowStartMinutes = timeToMinutes(window.startTime);
  const windowEndMinutes = timeToMinutes(window.endTime);
  const crossesMidnight = windowStartMinutes > windowEndMinutes;

  if (!crossesMidnight) {
    return eventMinutes >= windowStartMinutes && eventMinutes < windowEndMinutes;
  }

  return eventMinutes >= windowStartMinutes || eventMinutes < windowEndMinutes;
};

/**
 * The call window an event belongs to: the latest-starting window that contains it.
 *
 * Windows can overlap, so the search runs start-time descending — an event at 6:30pm on a day
 * with both 5pm-9pm and 6pm-10pm windows belongs to 6pm-10pm. The sort happens here rather than
 * being left to the caller so every site resolves a window the same way. Comparing an event's
 * stored start/end times against a window's is NOT equivalent: it disagrees for any event whose
 * times don't line up exactly with a window, e.g. one saved with a custom time.
 */
export const resolveEventWindow = (
  event: Event | undefined,
  windows: CallWindow[],
): CallWindow | undefined => {
  if (!event) return undefined;
  return [...windows]
    .sort((a, b) => b.startTime.localeCompare(a.startTime))
    .find((w) => isEventInWindow(event, w));
};

/**
 * Check if all windows are past for a given date
 */
export const areAllWindowsPast = (
  windows: CallWindow[],
  selectedDate: string,
  currentDate: string,
  currentTimeInMinutes: number,
): boolean => {
  if (windows.length === 0) return false;
  return windows.every((w) => isWindowPast(w, selectedDate, currentDate, currentTimeInMinutes));
};

/**
 * Find the first non-past window from a sorted list
 */
export const findFirstAvailableWindow = (
  windows: CallWindow[],
  selectedDate: string,
  currentDate: string,
  currentTimeInMinutes: number,
): CallWindow | null => {
  return (
    windows.find((w) => !isWindowPast(w, selectedDate, currentDate, currentTimeInMinutes)) ?? null
  );
};

/**
 * Validate that a time range has non-zero duration.
 * Supports overnight bookings (e.g., 18:00-03:00 is valid).
 * Only invalid case: start === end (0 duration).
 */
export const isValidTimeRange = (startTime: string, endTime: string): boolean => {
  // Any combination is valid for overnight support, except 0 duration
  return timeToMinutes(startTime) !== timeToMinutes(endTime);
};
